/**
 * LX-1 — LLW-FOLLOWUP-SEMANTIC-DEDUP.
 *
 * The LL5 orchestrator already de-duplicates by stable id
 * (`<provider>:<identifier>`), but that doesn't catch the cross-provider
 * pattern we see in production: the SAME paper indexed in Crossref by
 * DOI AND in arXiv by preprint id AND in PubMed by PMID — three rows,
 * one paper. The id-based pass treats them as distinct because the
 * `<provider>:<identifier>` keys differ.
 *
 * This module is the post-fetch semantic dedup. Pure, no I/O, no LLM
 * calls — entirely cost-free.
 *
 * Heuristic (per the LX-1 brief):
 *
 *   Two entries are duplicates iff EITHER:
 *     A. They share a normalised DOI (case-insensitive, leading
 *        `https://doi.org/` stripped), OR
 *     B. They have an explicit same-work/version DOI relationship, OR
 *     C. At least one DOI is absent, their normalised titles are similar,
 *        their numeric tokens agree and the first author surname overlaps.
 *
 *   The author overlap guard is important: many papers in the same
 *   sub-field share verbatim title prefixes ("Small modular reactor
 *   licensing"); we don't want those collapsed. Conversely, a true
 *   cross-provider match has the SAME authors AND title.
 *
 * Selection rule when a duplicate group is found:
 *   1. Prefer the latest numeric version within one arXiv work.
 *   2. Prefer explicitly recorded journal/conference form to a preprint.
 *   3. Then use relevance, provider metadata richness and year as tie-breaks.
 *   Integrity signals and query provenance are merged, never discarded.
 *
 * Pure helper. Same input shape (`LiteratureEntry[]`) in / out. The
 * orchestrator calls this AFTER its existing id-based pass and BEFORE
 * the relevance-sort + cap step, so the cap counts unique papers, not
 * duplicate hits.
 */
import type { LiteratureEntry } from '../types/siteEvidence';
import { literaturePublicationStatus, mergeLiteraturePublication } from './literaturePublication';

/* ── Tunable constants ────────────────────────────────────────────── */

/**
 * Minimum similarity (in [0,1]) for two normalised titles to be flagged
 * as the same paper. Calibrated against ~30 hand-labelled cross-provider
 * pairs sampled from production LLW runs (Redhead + a handful of EU
 * sites): 0.92 catches typo / punctuation / sub-title-suffix variants
 * while keeping legitimate same-topic siblings ("SMR licensing — Part 1"
 * vs "SMR licensing — Part 2") separate. Lower thresholds (0.85) collapse
 * series papers; higher (0.96) miss real duplicates with a single-word
 * sub-title difference.
 */
export const TITLE_SIMILARITY_THRESHOLD = 0.92;

/** Provider priority for the dedup selection tie-breaker. Higher wins. */
const PROVIDER_PRIORITY: Record<LiteratureEntry['provider'], number> = {
  crossref: 3,
  pubmed: 2,
  arxiv: 1,
};

/* ── Public types ─────────────────────────────────────────────────── */

export interface DedupLiteratureResult {
  /** The entries that survived the dedup pass, in input order
   *  (stable wrt. the first occurrence of each duplicate group). */
  entries: LiteratureEntry[];
  /** Number of entries removed by this pass. Useful for telemetry. */
  duplicatesRemoved: number;
}

/* ── Normalisation helpers (pure) ─────────────────────────────────── */

/**
 * Normalise a DOI for comparison.
 *
 *  - Lower-case the entire string (DOIs are case-insensitive per the
 *    DOI Handbook §2.4 — `10.1234/AbC` and `10.1234/abc` are the same).
 *  - Strip a leading `https://doi.org/` (or `http://`, `dx.doi.org/`).
 *  - Strip surrounding whitespace.
 *
 * Returns the empty string for `undefined` / blank / clearly-malformed
 * DOIs (those without a `/`) so the caller can treat missing-DOI as
 * "no DOI match" without extra null checks.
 */
export function normaliseDoi(doi: string | undefined): string {
  if (!doi) return '';
  let s = doi.trim().toLowerCase();
  // Strip common URL prefixes.
  s = s.replace(/^https?:\/\/(dx\.)?doi\.org\//, '');
  // A real DOI starts with `10.` and contains a `/`. Skip anything else
  // (a malformed `doi: see appendix` would otherwise match a stray
  // empty string).
  if (!s.startsWith('10.') || !s.includes('/')) return '';
  return s;
}

/**
 * Normalise a paper title for Levenshtein comparison.
 *
 *  - Lower-case.
 *  - Strip punctuation (replace any non-alphanumeric+space with space).
 *  - Collapse runs of whitespace to a single space.
 *  - Trim ends.
 *  - Drop common subtitle separator residue ("—", "–", ":") which the
 *    punctuation strip above already handles, but keep this comment as
 *    a marker for the convention.
 *
 * Returns an empty string for `undefined` / blank titles so the caller
 * can short-circuit.
 */
export function normaliseTitle(title: string | undefined): string {
  if (!title) return '';
  return title
    .toLowerCase()
    // Replace any character that isn't ASCII letter/digit/space with a space.
    // Unicode letters get folded out — acceptable for English-dominant
    // scientific literature; non-Latin titles still compare byte-for-byte
    // by their normalised form (everything becomes spaces and the title
    // collapses to empty, which short-circuits the title heuristic).
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * First author's surname, lower-cased and trimmed. Used as the
 * additional guard against collapsing same-topic-different-paper pairs
 * (see module header). Falls back to the bare lowercased author string
 * when no comma-separated surname is detectable.
 */
export function firstAuthorSurname(authors: ReadonlyArray<string>): string {
  const first = authors[0];
  if (!first) return '';
  const trimmed = first.trim();
  if (trimmed.length === 0) return '';
  // Common formats: "Smith, J." or "John Smith" or "Smith J.".
  // The cheapest robust rule: take the longest space-separated token
  // that's ≥ 3 chars and starts with a letter. That picks `Smith`
  // out of both `Smith, J.` and `J. Smith` and `Smith J.`.
  const tokens = trimmed
    .replace(/[.,]/g, ' ')
    .split(/\s+/)
    .filter(t => t.length >= 3 && /^[a-z]/i.test(t));
  if (tokens.length === 0) return trimmed.toLowerCase();
  // Pick the longest token (heuristic: surnames tend to be longer than
  // initials). On ties, the first wins.
  let best = tokens[0]!;
  for (const t of tokens) {
    if (t.length > best.length) best = t;
  }
  return best.toLowerCase();
}

/* ── Levenshtein distance (pure, O(n*m) memory-light) ─────────────── */

/**
 * Standard two-row Levenshtein distance. `O(n*m)` time, `O(min(n,m))`
 * memory. Returns the edit count needed to turn `a` into `b`.
 *
 * Short-circuits for empty inputs so callers don't divide by zero in
 * the similarity helper below.
 */
export function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;
  // Ensure `a` is the shorter of the two — keeps memory at min(n,m)+1.
  if (a.length > b.length) {
    const tmp = a;
    a = b;
    b = tmp;
  }
  const m = a.length;
  const n = b.length;
  // Two-row buffer
  let prev = new Array<number>(m + 1);
  let curr = new Array<number>(m + 1);
  for (let i = 0; i <= m; i++) prev[i] = i;
  for (let j = 1; j <= n; j++) {
    curr[0] = j;
    const bj = b.charCodeAt(j - 1);
    for (let i = 1; i <= m; i++) {
      const cost = a.charCodeAt(i - 1) === bj ? 0 : 1;
      const del = prev[i]! + 1;
      const ins = curr[i - 1]! + 1;
      const sub = prev[i - 1]! + cost;
      let best = del < ins ? del : ins;
      if (sub < best) best = sub;
      curr[i] = best;
    }
    // Swap rows for next iteration.
    const tmp = prev;
    prev = curr;
    curr = tmp;
  }
  return prev[m]!;
}

/**
 * Title similarity in `[0, 1]`. `1` = exact match (after normalisation),
 * `0` = entirely different. Defined as `1 - editDistance / maxLen` so
 * the threshold has an intuitive scale.
 *
 * Returns `0` when EITHER input normalises to empty (the title heuristic
 * is then unusable; caller falls back to DOI matching).
 */
export function titleSimilarity(a: string, b: string): number {
  if (a.length === 0 || b.length === 0) return 0;
  const maxLen = a.length > b.length ? a.length : b.length;
  if (maxLen === 0) return 0;
  const d = levenshtein(a, b);
  const sim = 1 - d / maxLen;
  return sim < 0 ? 0 : sim;
}

/* ── Pairwise duplicate test (pure) ───────────────────────────────── */

/**
 * Return `true` iff entries `a` and `b` are the same paper per the
 * heuristic in the module header. Exposed for unit-testing the
 * individual decision.
 */
export function entriesAreDuplicates(a: LiteratureEntry, b: LiteratureEntry): boolean {
  // Same id → already deduped upstream, but treat as duplicate defensively.
  if (a.id === b.id) return true;
  // DOI match — definitive when both sides carry a normalised DOI.
  const doiA = normaliseDoi(a.doi);
  const doiB = normaliseDoi(b.doi);
  if (doiA.length > 0 && doiA === doiB) return true;
  // Editorial notices are separate records, never another version of the study.
  if (literaturePublicationStatus(a) === 'notice' || literaturePublicationStatus(b) === 'notice') return false;
  const relatedA = (a.publication?.relatedDois ?? []).map(normaliseDoi).filter(Boolean);
  const relatedB = (b.publication?.relatedDois ?? []).map(normaliseDoi).filter(Boolean);
  if ((doiB && relatedA.includes(doiB)) || (doiA && relatedB.includes(doiA))) return true;
  // Distinct DOI records require a declared relationship. Similar titles alone
  // must not collapse separate trials, series papers or an unverified revision.
  if (doiA && doiB) return false;
  // Title + first-author similarity fallback for entries without a
  // shared DOI (arXiv preprints often lack a Crossref DOI until the
  // journal version is published).
  const titleA = normaliseTitle(a.title);
  const titleB = normaliseTitle(b.title);
  if (titleA.length === 0 || titleB.length === 0) return false;
  if (JSON.stringify(titleA.match(/\d+/g) ?? []) !== JSON.stringify(titleB.match(/\d+/g) ?? [])) return false;
  const sim = titleSimilarity(titleA, titleB);
  if (sim < TITLE_SIMILARITY_THRESHOLD) return false;
  const surnameA = firstAuthorSurname(a.authors);
  const surnameB = firstAuthorSurname(b.authors);
  // Author guard. We accept the duplicate flag iff:
  //   - both have a surname AND they're equal, OR
  //   - one surname is missing and title similarity is unambiguous (≥0.98).
  //     Two known different authors are never ignored because titles match.
  if (surnameA.length > 0 && surnameB.length > 0 && surnameA === surnameB) return true;
  if ((!surnameA || !surnameB) && sim >= 0.98) return true;
  return false;
}

/* ── Selection rule (which entry survives a duplicate group) ──────── */

/**
 * Score an entry for the "which copy survives" decision. Higher wins.
 * Composed as `score * 1000 + providerPriority * 10 + (year - 1900)`
 * so relevance dominates, provider priority is the next tiebreak, and
 * recency is the final tiebreak. Pure.
 */
function entrySelectionScore(e: LiteratureEntry): number {
  const rel = Math.max(0, Math.min(1, e.relevanceScore ?? 0));
  const prov = PROVIDER_PRIORITY[e.provider] ?? 0;
  const yr = Number.isFinite(e.year) ? Math.max(0, e.year - 1900) : 0;
  return rel * 1000 + prov * 10 + yr / 100;
}

function prefersEntry(candidate: LiteratureEntry, current: LiteratureEntry): boolean {
  if (candidate.provider === 'arxiv' && current.provider === 'arxiv' && candidate.id === current.id) {
    const version = (e: LiteratureEntry) => e.publication?.version ?? (Number(e.identifier.match(/v(\d+)$/)?.[1]) || 0);
    if (version(candidate) !== version(current)) return version(candidate) > version(current);
  }
  // Bibliographic form is explicit metadata, never inferred from provider name.
  const formRank = (e: LiteratureEntry) => e.publication?.kind === 'journal-article' ? 3
    : e.publication?.kind === 'conference-paper' ? 2
    : e.publication?.kind === 'preprint' ? 1 : 0;
  if (formRank(candidate) !== formRank(current)) return formRank(candidate) > formRank(current);
  return entrySelectionScore(candidate) > entrySelectionScore(current);
}

/* ── Public entry point ──────────────────────────────────────────── */

/**
 * Semantically de-duplicate a list of literature entries.
 *
 * Stable wrt. the first occurrence of each duplicate group: when a
 * duplicate group is identified, the SURVIVOR is placed at the position
 * of the FIRST member of the group (so callers that depend on the
 * source-pass order — e.g. Crossref first, then arXiv — see a sensible
 * collapse).
 *
 * Candidate lists are bounded upstream. Pairwise title comparison is O(n²);
 * successful unions additionally inspect groups to prevent DOI-less copies
 * from transitively joining distinct DOI records.
 *
 * @param entries  Input entries, in the order the orchestrator
 *                 aggregated them (typically Crossref → arXiv → PubMed).
 */
export function dedupLiteratureEntries(
  entries: ReadonlyArray<LiteratureEntry>,
): DedupLiteratureResult {
  const n = entries.length;
  if (n <= 1) {
    return { entries: entries.slice(), duplicatesRemoved: 0 };
  }

  // Union-find — each duplicate group is a connected component under
  // the symmetric `entriesAreDuplicates` relation. O(n^2 * α(n)) which
  // for n ≤ 36 is trivial.
  const parent = new Array<number>(n);
  for (let i = 0; i < n; i++) parent[i] = i;
  const find = (i: number): number => {
    let r = i;
    while (parent[r] !== r) r = parent[r]!;
    // Path compression.
    let cur = i;
    while (parent[cur] !== r) {
      const next = parent[cur]!;
      parent[cur] = r;
      cur = next;
    }
    return r;
  };
  const union = (i: number, j: number): void => {
    const ri = find(i);
    const rj = find(j);
    if (ri === rj) return;
    const left = entries.filter((_, index) => find(index) === ri);
    const right = entries.filter((_, index) => find(index) === rj);
    const leftDois = left.map(e => normaliseDoi(e.doi)).filter(Boolean);
    const rightDois = right.map(e => normaliseDoi(e.doi)).filter(Boolean);
    // A DOI-less copy must not bridge two different DOI records transitively.
    if (leftDois.length && rightDois.length && !leftDois.some(d => rightDois.includes(d))) {
      const linked = left.some(a => right.some(b => {
        const aDoi = normaliseDoi(a.doi);
        const bDoi = normaliseDoi(b.doi);
        return (bDoi && (a.publication?.relatedDois ?? []).some(d => normaliseDoi(d) === bDoi))
          || (aDoi && (b.publication?.relatedDois ?? []).some(d => normaliseDoi(d) === aDoi));
      }));
      if (!linked) return;
    }
    parent[ri] = rj;
  };
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      if (entriesAreDuplicates(entries[i]!, entries[j]!)) union(i, j);
    }
  }

  // For each component, pick the strongest representative (highest
  // entrySelectionScore). Track the FIRST input position of the
  // component so the output is stable wrt. input order (the survivor
  // takes the slot of the group's first occurrence).
  interface GroupInfo {
    /** First-occurrence position of the group (output ordering anchor). */
    firstPos: number;
    /** Index of the chosen survivor entry. */
    survivor: number;
    members: number[];
  }
  const groups = new Map<number, GroupInfo>();
  for (let i = 0; i < n; i++) {
    const root = find(i);
    const existing = groups.get(root);
    if (!existing) {
      groups.set(root, { firstPos: i, survivor: i, members: [i] });
      continue;
    }
    // Pick the higher-scored entry as the survivor; tie-break by the
    // original input order (earlier wins) so behaviour is deterministic
    // across providers with identical relevance scores.
    existing.members.push(i);
    if (prefersEntry(entries[i]!, entries[existing.survivor]!)) existing.survivor = i;
  }

  // Emit survivors in firstPos order — preserves the "stable wrt. first
  // occurrence" contract documented at the top of this function.
  const ordered = Array.from(groups.values()).sort((a, b) => a.firstPos - b.firstPos);
  const out = ordered.map(g => mergeLiteraturePublication(entries[g.survivor]!, g.members.map(i => entries[i]!)));
  return { entries: out, duplicatesRemoved: n - out.length };
}
